import { useEffect, useState } from 'react';
import { AlertHost } from '@/ui';
import { Providers } from '@/features/auth/Providers';
import { useAuthStore } from '@/features/auth/authStore';
import { useSettingsStore } from '@/features/settings/settingsStore';
import { Splash } from '@/components/ui/Splash';
import { LockGate } from '@/features/biometric/LockGate';
import { ConsentGate } from '@/features/consents/ConsentGate';
import { useAdTracker } from '@/features/ads/hooks';
import { installPageScroll } from '@/lib/pageScroll';
import { AppRoutes } from '@/navigation/routes';
// side-effect import: initialises i18next
import i18n, { ensureCatalog, resolveInitialLanguage, syncLayoutDirection } from '@/i18n';

/**
 * The root of the app — mobile's `app/_layout.tsx`, minus the parts that are
 * native-only concerns (font loading, the portrait orientation lock, and the
 * push-notification tap handler; see features/push/register.ts for why there is
 * no push here).
 */
export default function App() {
  return (
    <Providers>
      <RootNavigator />
    </Providers>
  );
}

function RootNavigator() {
  const bootstrap = useAuthStore((s) => s.bootstrap);
  // Keeps the batched ad impression/click flush loop running for the session.
  useAdTracker();
  const hasHydrated = useSettingsStore((s) => s._hasHydrated);
  const language = useSettingsStore((s) => s.language);
  const [langReady, setLangReady] = useState(false);

  // Once the persisted preferences hydrate, fetch and apply the saved language
  // (i18next starts on English) and align the document's direction (RTL for
  // Arabic) before first paint — so a saved language never flashes as English
  // and an Arabic layout never flashes left-to-right.
  useEffect(() => {
    if (!hasHydrated) return;
    const target = resolveInitialLanguage(language);
    void ensureCatalog(target)
      .then(() => (target === i18n.language ? undefined : i18n.changeLanguage(target)))
      .then(() => {
        syncLayoutDirection();
        setLangReady(true);
      });
    // Run once post-hydration; later switches go through setAppLanguage.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [hasHydrated]);

  // Restore the session from the stored refresh token once, on launch.
  useEffect(() => {
    bootstrap();
  }, [bootstrap]);

  // A screen owns its scrolling, but the window is wider than a screen's
  // measure — so a wheel out in the margin has to be forwarded to it.
  useEffect(() => installPageScroll(), []);

  // Wait for the persisted theme preference and the language before first
  // paint, so a pinned Light/Dark choice never flashes as "System" and a saved
  // language never flashes as the browser default.
  if (!hasHydrated || !langReady) {
    return (
      <div className="app-root">
        <div className="app-shell">
          <Splash />
        </div>
      </div>
    );
  }

  return (
    <div className="app-root">
      <div className="app-shell">
        <AppRoutes />
      </div>
      <LockGate />
      {/* SEC-18: asks for an outstanding acceptance before the fan meets the
          server-side gate at checkout. Reading stays available either way. */}
      <ConsentGate />
      {/* Alert.alert() renders here rather than as the browser's own dialog. */}
      <AlertHost />
    </div>
  );
}
